/**
 * PATH: packages/email-hub/src/feature/hooks/useEmails.metaCache.ts
 * FILE_ID: EMAILHUB-FEATURE-USE-EMAILS-METACACHE-001
 * PURPOSE: SWR-style persistence for small email-hub "meta" payloads
 *          (mailbox accounts list, filter facets) so the hub shell
 *          paints with data instantly on mount instead of waiting
 *          ~1-1.5s (measured client p50 across 64k calls/7d) for
 *          GET /api/email-accounts/ + /api/emails/facets/.
 * WHY: PERF-2026-07-08-EMAIL-BOOT-META-CACHE (load-speed audit R5).
 *      The inbox LIST already had a persisted seed
 *      (useEmails.listCache.ts) — the mailbox switcher and filter
 *      sidebar still cold-fetched every mount, so the shell assembled
 *      piecemeal over several seconds. Same SWR contract: seed is a
 *      pure first-paint placeholder, the normal fetch overwrites it
 *      in place.
 * SAFETY (mirrors useEmails.listCache.ts):
 *   - Keyed by the tab's pinned user id (``quotech_tab_user_id``,
 *     the cross-tab identity guard's authority) so a re-login can't
 *     paint the previous user's mailboxes; a snapshot whose user key
 *     doesn't match the current pin reads as a miss.
 *   - Swept by ``resetEmailListCache`` (identity change / logout)
 *     alongside the inbox list snapshots.
 *   - Best-effort: storage/parse failure degrades to "no seed".
 *   - 24h max age — day-old dropdown options are useless.
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────────┐   ┌──────────────────┐
 *   │ kind + value │ → │ localStorage JSON    │ → │ readEmailMeta on │
 *   │ (post-fetch) │   │ (versioned+user-key) │   │ next mount seeds │
 *   └──────────────┘   └─────────────────────┘   └──────────────────┘
 * DEPENDENCIES: none (web storage only).
 * RELATED:
 *   - ./useEmails.listCache.ts: the inbox-rows sibling of this cache.
 *   - ./useEmails.cache.ts: resetEmailListCache wires the sweep.
 */

import { getEmailMailboxScope, getEmailTenantId } from '@quotech/tenant-shared/email/api'

const KEY_PREFIX = 'qf:email-meta:'
// Bump if a persisted shape changes incompatibly.
// v2 (BUG-2026-07-17-EMAIL-ORG-SWITCH-STALE-MAILBOX): snapshots now
// carry the organisation (tenant) id they were written under.
// v3 (RL-01 Phase 1): also carry mailbox visibility scope so a
// solo-mailbox filter cannot paint another mailbox's meta seed.
const SNAPSHOT_VERSION = 3
// Meta payloads are cheap to recompute and confusing when ancient —
// same online cap as the inbox list seed.
const MAX_AGE_MS = 24 * 60 * 60 * 1000

interface MetaSnapshot<T> {
  v: number
  at: number
  /** Tab-pinned user id the snapshot was written under. */
  u: string
  /** Organisation (tenant) id the snapshot was written under.
   *  '' when the host has no tenant binding (single-org hosts). */
  o: string
  /** Mailbox visibility scope fingerprint (RL-01). '' = all mailboxes. */
  m: string
  value: T
}

/** Current org dimension for snapshot keying — the module-scope
 *  tenant id every email request is issued with. Hosts publish it
 *  during render (ApiAuthGate / TenantBridge) BEFORE children mount,
 *  so reads here always see the org the surrounding UI is bound to. */
function tenantKey(): string {
  const id = getEmailTenantId()
  return id != null ? String(id) : ''
}

function mailboxKey(): string {
  return getEmailMailboxScope() ?? ''
}

function storage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage
    }
  } catch {
    // fall through
  }
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    return window.sessionStorage
  } catch {
    return null
  }
}

/** The cross-tab identity guard's tab-pinned user id — the same
 *  authority quotechApi enforces on every response
 *  (BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT). '' when not yet pinned
 *  (first ever request still in flight): we treat that as "unknown"
 *  and neither read nor write, so a snapshot can never be attributed
 *  to the wrong user. */
function tabUserKey(): string {
  try {
    return sessionStorage.getItem('quotech_tab_user_id') ?? ''
  } catch {
    return ''
  }
}

function keyFor(kind: string): string {
  return `${KEY_PREFIX}${kind}`
}

/** Persist a meta payload for the current tab identity. No-op when the
 *  identity isn't pinned yet or on any storage failure. */
export function persistEmailMeta<T>(kind: string, value: T): void {
  const s = storage()
  const u = tabUserKey()
  if (!s || !kind || !u) return
  try {
    const snapshot: MetaSnapshot<T> = {
      v: SNAPSHOT_VERSION,
      at: Date.now(),
      u,
      o: tenantKey(),
      m: mailboxKey(),
      value,
    }
    s.setItem(keyFor(kind), JSON.stringify(snapshot))
  } catch {
    // Quota / serialization failure — next mount just cold-fetches.
  }
}

/** Read a meta payload, or null when absent / stale / another user's /
 *  identity not pinned yet / corrupt. */
export function readEmailMeta<T>(kind: string): T | null {
  const s = storage()
  const u = tabUserKey()
  if (!s || !kind || !u) return null
  try {
    const raw = s.getItem(keyFor(kind))
    if (!raw) return null
    const parsed = JSON.parse(raw) as MetaSnapshot<T>
    if (
      !parsed ||
      parsed.v !== SNAPSHOT_VERSION ||
      parsed.u !== u ||
      parsed.o !== tenantKey() ||
      (parsed.m ?? '') !== mailboxKey() ||
      typeof parsed.at !== 'number' ||
      Date.now() - parsed.at > MAX_AGE_MS
    ) {
      return null
    }
    return parsed.value ?? null
  } catch {
    return null
  }
}

/** Drop every persisted email-meta snapshot in both stores. Wired into
 *  ``resetEmailListCache`` so identity change / logout sweeps these
 *  together with the inbox list snapshots. */
export function clearPersistedEmailMeta(): void {
  const stores: Array<Storage | null> = []
  try {
    stores.push(typeof window !== 'undefined' ? window.localStorage : null)
  } catch {
    stores.push(null)
  }
  try {
    stores.push(typeof window !== 'undefined' ? window.sessionStorage : null)
  } catch {
    stores.push(null)
  }
  for (const s of stores) {
    if (!s) continue
    try {
      const toRemove: string[] = []
      for (let i = 0; i < s.length; i += 1) {
        const k = s.key(i)
        if (k && k.startsWith(KEY_PREFIX)) toRemove.push(k)
      }
      for (const k of toRemove) s.removeItem(k)
    } catch {
      // ignore
    }
  }
}
